import { Button, Grid, Stack, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import DateRangePicker from '../../components/common/DateRangePicker'
import TextSearchField from '../../components/common/TextSearchField'
import ContractorTable from '../../components/contractor/ContractorTable'
import ScreenLoadingPlaceholder from '../../components/ScreenLoadingPlaceholder'
import { useAppDispatch } from '../../hooks/reduxHooks'
import { RouteConstants } from '../../navigation/navigation-types'
import { displayMessage } from '../../services/redux/snackbar/snackbar-actions'
import { ContractorBriefDto, ContractorDto, ContractorType } from '../../services/api/sharedTypes/Contractor'
import { ContractorManager } from '../../services/api/ContractorManager'
import CustomContainer from '../../components/common/CustomContainer'
import { convertToSearchString } from '../../utils/global-utils'
import LinkWrapper from '../../components/common/LinkWrapper'
import CancelFiltersButton from '../../components/common/CancelFiltersButton'

const WorkersAndCarriers: React.FC = () => {
    const dispatch = useAppDispatch()

    const [loadingData, setLoadingData] = useState<boolean>(false)
    const [dateFrom, setDateFrom] = useState<Date | null>(null)
    const [dateTo, setDateTo] = useState<Date | null>(null)
    const [searchedValue, setSearchedValue] = useState<string>('')
    const [contractors, setContractors] = useState<ContractorBriefDto[]>([])

    const removeContractor = async (id: string) => {
        if (!contractors) return
        try {
            setLoadingData(true)
            await ContractorManager.getManager().deleteContractorAsync(id)
            const newContractors = contractors.filter((contractor) => contractor.id !== id)
            if (newContractors) {
                setContractors(newContractors)
                setLoadingData(false)
            }
        } catch (error) {
            setLoadingData(false)
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
        setLoadingData(false)
        dispatch(displayMessage({ message: 'Pracovník smazán', type: 'success' }))
    }

    //Get contractors from server
    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true)

            try {
                const contractorsRes = await ContractorManager.getManager().getContractorListAsync()
                if (contractorsRes) {
                    setContractors(contractorsRes.contractors)
                }
            } catch (error) {
                setLoadingData(false)
                return dispatch(displayMessage({ message: error, type: 'error' }))
            }

            setLoadingData(false)
        }

        fetchData()
    }, [setLoadingData])

    useEffect(() => {
        const setFilteredClientsByEventDate = async () => {
            setLoadingData(true)
            if (dateTo && dateFrom) {
                try {
                    if (isNaN(dateTo.getTime()) || isNaN(dateFrom.getTime())) return setLoadingData(false)
                    const contractorRes = await ContractorManager.getManager().getAllFilteredContractorsAsync(
                        dateFrom.toISOString(),
                        dateTo.toISOString()
                    )
                    if (contractorRes) {
                        setContractors(contractorRes.contractors)
                    }
                } catch (error) {
                    return dispatch(displayMessage({ message: error, type: 'error' }))
                }
            } else {
                const contractorsRes = await ContractorManager.getManager().getContractorListAsync()
                if (contractorsRes) {
                    setContractors(contractorsRes.contractors)
                }
            }
            setLoadingData(false)
        }

        setFilteredClientsByEventDate()
    }, [dateFrom, dateTo, setLoadingData])

    const filteredContractors = contractors.filter(
        (contractor: ContractorBriefDto) => {
            if (searchedValue) {
                const name = contractor.name + ' ' + contractor.lastName
                return (
                    convertToSearchString(name).includes(convertToSearchString(searchedValue)) ||
                    convertToSearchString(contractor.note).includes(convertToSearchString(searchedValue))
                )
            }
            return true
        },
        [dateFrom, dateTo, searchedValue]
    )

    const workers = filteredContractors.filter((contractor: ContractorDto) => contractor.type === ContractorType.Brigadier)
    const carriers = filteredContractors.filter((contractor: ContractorDto) => contractor.type === ContractorType.Carrier)

    if (loadingData) return <ScreenLoadingPlaceholder />

    return (
        <CustomContainer>
            <Stack justifyContent='space-between' direction='row'>
                <Typography variant='h5'>Brigádníci a dopravci</Typography>
                <LinkWrapper route={RouteConstants.addContact}>
                    <Button variant='contained'>+ Přidat kontakt</Button>
                </LinkWrapper>
            </Stack>

            <Stack direction='row' gap={3} mt={1} mb={1} overflow='hidden'>
                <DateRangePicker
                    labelFrom='Události od'
                    labelTo='Události do'
                    dateTo={dateTo}
                    dateFrom={dateFrom}
                    onSetDateFrom={setDateFrom}
                    onSetDateTo={setDateTo}
                />
                <TextSearchField value={searchedValue} onChange={(e) => setSearchedValue(e.target.value)} />
                {
                    searchedValue || dateFrom || dateTo ? (
                        <CancelFiltersButton onClick={
                            () => {
                                setSearchedValue('')
                                setDateFrom(null)
                                setDateTo(null)
                            }
                        }
                        />
                    ) : null
                }
            </Stack>

            <Grid container rowSpacing={4} columnSpacing={4}>
                <Grid item xs={12} lg={6} sx={{ overflow: 'auto', minWidth: { lg: '669px' } }}>
                    <ContractorTable
                        title='Brigádníci'
                        contracts={workers}
                        onDelete={removeContractor}
                    />
                </Grid>
                <Grid item xs={12} lg={6} sx={{ overflow: 'auto', minWidth: { lg: '669px' } }}>
                    <ContractorTable
                        title='Dopravci'
                        contracts={carriers}
                        onDelete={removeContractor}
                    />
                </Grid>
            </Grid>
        </CustomContainer>
    )
}

export default WorkersAndCarriers
